import { canonicalOrigin, canonicalUrl, toAbsoluteUrl } from '@/lib/seo/canonical'
import { DEFAULT_OG_IMAGE_PATH } from '@/lib/seo/images'
import { siteConfig, socialConfig } from '@/constants/site'

/**
 * WHAT EACH PAGE IS, told to machines.
 *
 * Pure and browser-safe. Every node here is built from values that are ALREADY
 * VISIBLE on the page it describes. Structured data that says something the
 * visitor cannot see is the one kind that actively costs you: Google treats it
 * as spam and an answer engine treats it as untrustworthy.
 *
 * Three rules hold, and they are why this is a module rather than a literal at
 * each call site:
 *
 *   1. NOTHING IS INVENTED. There is no `brand`, no `sku`, no `gtin`, no
 *      `itemCondition`, no `aggregateRating` and no `address` anywhere below,
 *      because this database holds none of them. A field we cannot source from
 *      a row does not appear, rather than appearing with a plausible guess.
 *   2. ONE GRAPH PER PAGE, with stable `@id`s. `Organization` is defined once
 *      and every other node points at it by `@id`, so the whole site resolves
 *      to one entity rather than to a new company on every page.
 *   3. TYPE FOLLOWS THE THING. A phone is a `Product`. A Telegram username, a
 *      social account, a channel, a service and a job are not, and forcing
 *      `Product` onto them to chase a rich result is exactly the kind of
 *      contradiction that gets structured data ignored site-wide.
 */

export type JsonLdNode = Record<string, unknown>

export const ORGANIZATION_ID = '#organization'
export const WEBSITE_ID = '#website'

const idFor = (origin: string, fragment: string): string => `${origin}/${fragment}`

/**
 * The publisher, defined once for the whole site.
 *
 * `sameAs` lists only profiles that are actually configured and actually
 * linked from the footer, so the machine-readable identity and the human one
 * agree. An unset handle is omitted rather than guessed at.
 */
export const organizationNode = (baseUrl?: string | null): JsonLdNode => {
    const origin = canonicalOrigin(baseUrl)
    const sameAs = [socialConfig.telegram, socialConfig.instagram].filter((value): value is string => Boolean(value))

    return {
        '@type': 'Organization',
        '@id': idFor(origin, ORGANIZATION_ID),
        name: siteConfig.name,
        url: `${origin}/`,
        description: siteConfig.description,
        logo: {
            '@type': 'ImageObject',
            url: toAbsoluteUrl(baseUrl, '/images/brand/primary-logo.png'),
            width: 2172,
            height: 724
        },
        image: toAbsoluteUrl(baseUrl, DEFAULT_OG_IMAGE_PATH),
        ...(sameAs.length > 0 ? { sameAs } : {})
    }
}

/**
 * The site itself. `potentialAction` names the search that really exists at
 * `/browse?q=`, which is the platform-wide discovery query rather than any one
 * marketplace's.
 */
export const webSiteNode = (baseUrl?: string | null): JsonLdNode => {
    const origin = canonicalOrigin(baseUrl)

    return {
        '@type': 'WebSite',
        '@id': idFor(origin, WEBSITE_ID),
        name: siteConfig.name,
        url: `${origin}/`,
        description: siteConfig.description,
        publisher: { '@id': idFor(origin, ORGANIZATION_ID) },
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${origin}/browse?q={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
        }
    }
}

export type BreadcrumbStep = {
    name: string
    /** A site path. The last step is the page itself and may omit it. */
    path?: string | null
}

/**
 * The trail, mirroring the one the visitor can see.
 *
 * `/listing/[slug]` and `/category/[slug]` both render a visible breadcrumb and
 * neither described it to a machine, which is the one case where the markup is
 * free: the data is already on the page.
 */
export const breadcrumbNode = (baseUrl: string | null | undefined, steps: Array<BreadcrumbStep>): JsonLdNode | null => {
    const usable = steps.filter((step) => step.name.trim().length > 0)

    if (usable.length < 2) return null

    return {
        '@type': 'BreadcrumbList',
        itemListElement: usable.map((step, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: step.name,
            /*
             * `toAbsoluteUrl` rather than `canonicalUrl`, because a breadcrumb
             * step is a real link the visitor can follow and a filtered
             * category view IS that link. A canonical strips the query string
             * by design, which would collapse every category onto one URL.
             */
            ...(step.path ? { item: toAbsoluteUrl(baseUrl, step.path) } : {})
        }))
    }
}

/**
 * Categories whose listings are NOT physical products.
 *
 * A username, a social account, a YouTube channel, a software licence, a
 * service, a job and a property are all legitimately for sale here and none of
 * them is a `Product` in schema.org's sense. `other` is on the list because it
 * is a mixed bag: an unknown item defaults to the honest weaker type rather
 * than to the one with the richer result.
 */
const NON_PRODUCT_CATEGORIES = new Set([
    'usernames',
    'social-accounts',
    'youtube-channels',
    'influencers',
    'crypto-p2p',
    'digital-creative',
    'software-licences',
    'services',
    'jobs-gigs',
    'real-estate',
    'other'
])

export type ListingSeoFacts = {
    title: string
    description: string
    url: string
    images: Array<string>
    price: number | null
    currency: string
    priceType: string
    listingType: string
    categoryName: string | null
    categorySlug: string | null
    status: string
    publishedAt?: string | null
    /** The marketplace it belongs to, named only when it is public. */
    communityName?: string | null
    /** The seller's public display name. Never an id, never a phone number. */
    sellerName?: string | null
    /** BCP-47 tag of the language the seller wrote in, when it was determined. */
    inLanguage?: string | null
}

/** Only a listing that is genuinely still available may say so. */
const availabilityFor = (status: string): string => {
    if (status === 'active') return 'https://schema.org/InStock'
    if (status === 'sold') return 'https://schema.org/SoldOut'

    return 'https://schema.org/Discontinued'
}

/**
 * The offer, which is the part that is true whatever the thing is.
 *
 * A price is stated only when there is one. `contact` and `free` listings are
 * real states here, and a missing price is left out entirely rather than sent
 * as `0`, which would advertise a free phone.
 */
const offerNode = (facts: ListingSeoFacts): JsonLdNode => ({
    '@type': 'Offer',
    url: facts.url,
    availability: availabilityFor(facts.status),
    ...(facts.price !== null && facts.priceType !== 'contact'
        ? { price: facts.price, priceCurrency: facts.currency }
        : {}),
    ...(facts.sellerName
        ? { seller: { '@type': 'Person', name: facts.sellerName } }
        : {})
})

/**
 * What this listing IS.
 *
 * A physical item for sale is a `Product` carrying its `Offer`. Everything else
 * is described as the page it is, with the same `Offer` attached through
 * `mainEntity`, which states the price and availability without claiming the
 * thing is a manufactured good.
 */
export const listingNode = (facts: ListingSeoFacts): JsonLdNode => {
    const digital = facts.categorySlug !== null && NON_PRODUCT_CATEGORIES.has(facts.categorySlug)
    const sellingSomething = facts.listingType === 'selling'
    const offer = offerNode(facts)

    const shared = {
        name: facts.title,
        description: facts.description,
        url: facts.url,
        ...(facts.images.length > 0 ? { image: facts.images } : {}),
        ...(facts.inLanguage ? { inLanguage: facts.inLanguage } : {})
    }

    if (!digital && sellingSomething) {
        return {
            '@type': 'Product',
            ...shared,
            ...(facts.categoryName ? { category: facts.categoryName } : {}),
            offers: offer
        }
    }

    return {
        '@type': 'WebPage',
        ...shared,
        ...(facts.publishedAt ? { datePublished: facts.publishedAt } : {}),
        mainEntity: {
            '@type': 'Offer',
            ...offer,
            itemOffered: {
                '@type': 'Thing',
                name: facts.title,
                ...(facts.categoryName ? { additionalType: facts.categoryName } : {})
            }
        }
    }
}

export type CollectionFacts = {
    name: string
    description: string
    url: string
    /** Only when the number is real and public. */
    itemCount?: number | null
    /**
     * The entries this page actually shows, in order. Each becomes a bare
     * `ListItem` with a position, a URL and a name, nothing more: the listing's
     * own page carries its `Product` or `Offer`, and repeating price or
     * availability here would be a second copy that can drift from the first.
     */
    items?: Array<{ position: number, url: string, name: string }>
    image?: string | null
    inLanguage?: string | null
}

/**
 * A page that lists other pages: a marketplace storefront, a category, a browse
 * result. `CollectionPage` is the honest type for all three.
 *
 * `OnlineStore` and `Organization` are deliberately NOT used for a community.
 * A Telegram group that posts listings is not a registered business, and
 * claiming it is one would be a fabrication of exactly the sort this module
 * refuses everywhere else.
 */
export const collectionPageNode = (baseUrl: string | null | undefined, facts: CollectionFacts): JsonLdNode => {
    const origin = canonicalOrigin(baseUrl)

    return {
        '@type': 'CollectionPage',
        name: facts.name,
        description: facts.description,
        url: facts.url,
        isPartOf: { '@id': idFor(origin, WEBSITE_ID) },
        ...(facts.image ? { image: facts.image } : {}),
        ...(facts.inLanguage ? { inLanguage: facts.inLanguage } : {}),
        ...(typeof facts.itemCount === 'number' || (facts.items && facts.items.length > 0)
            ? {
                mainEntity: {
                    '@type': 'ItemList',
                    ...(typeof facts.itemCount === 'number' ? { numberOfItems: facts.itemCount } : {}),
                    ...(facts.items && facts.items.length > 0
                        ? {
                            itemListOrder: 'https://schema.org/ItemListOrderDescending',
                            itemListElement: facts.items.map((item) => ({
                                '@type': 'ListItem',
                                position: item.position,
                                url: item.url,
                                name: item.name
                            }))
                        }
                        : {})
                }
            }
            : {})
    }
}

/**
 * One script per page, as `@graph`.
 *
 * Several `<script type="application/ld+json">` blocks on one page are legal
 * but make the entity relationships harder for a parser to join up. Empty
 * nodes are dropped so a caller can pass an optional breadcrumb without
 * guarding it.
 */
export const jsonLdGraph = (nodes: Array<JsonLdNode | null | undefined>): JsonLdNode | undefined => {
    const graph = nodes.filter((node): node is JsonLdNode => Boolean(node))

    if (graph.length === 0) return undefined

    return { '@context': 'https://schema.org', '@graph': graph }
}

/**
 * Each character that means something to the HTML parser, mapped to the JSON
 * escape that means exactly the same character to a JSON parser.
 *
 * U+2028 and U+2029 are included because they are legal inside a JSON string
 * but are line terminators to older JavaScript parsers.
 *
 * Both are built from their code points on purpose. Written as raw characters
 * they are invisible in an editor and, inside a regular expression literal,
 * they END the line, so the module stops parsing ("Invalid regular expression:
 * missing /") and every page that renders `<Seo>` fails to load.
 */
const LINE_SEPARATOR = String.fromCharCode(0x2028)
const PARAGRAPH_SEPARATOR = String.fromCharCode(0x2029)

const JSON_LD_ESCAPES: Record<string, string> = {
    '<': '\\u003c',
    '>': '\\u003e',
    '&': '\\u0026',
    [LINE_SEPARATOR]: '\\u2028',
    [PARAGRAPH_SEPARATOR]: '\\u2029'
}

const JSON_LD_UNSAFE = new RegExp(`[<>&${LINE_SEPARATOR}${PARAGRAPH_SEPARATOR}]`, 'g')

/**
 * THE ONLY WAY STRUCTURED DATA BECOMES TEXT INSIDE A `<script>` ELEMENT.
 *
 * `JSON.stringify` alone is NOT safe inside HTML. The HTML parser ends a
 * script element at the first `</script` it meets, whatever JSON string it
 * sits inside, so a listing titled `</script><script>alert(1)</script>` would
 * close the block and run as a second, real script. Titles, descriptions and
 * names here are typed by sellers and posted in Telegram groups, so this is
 * an injection point, not a theoretical one.
 *
 * After serialising, `<`, `>` and `&` are rewritten as their JSON unicode
 * escapes (backslash, then u003c, u003e and u0026), never as HTML entities
 * such as `&lt;`. Those are JSON escapes, so a JSON parser (and so
 * every search engine) reads back exactly the original characters, while the
 * HTML parser never sees a tag. The three characters can only ever appear
 * inside a JSON string, so the rewrite cannot touch the document's structure,
 * and every other character, Albanian letters included, passes through as is.
 *
 * `<Seo>` is the one caller. Never render `JSON.stringify` into a script
 * element anywhere else.
 */
export const serializeJsonLd = (value: unknown): string =>
    (JSON.stringify(value) ?? 'null').replace(JSON_LD_UNSAFE, (character) => JSON_LD_ESCAPES[character] ?? character)
